/**
 * WelcomeTour — three-step spotlight tour for brand-new admins (Jun 2026).
 * Highlights Jobs, Team Management and Settings in the sidebar on first
 * login. Self-gating: admins only, desktop only, fresh companies only
 * (onboarding all_core_done=false), shown once (localStorage).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePromptSlot } from '../utils/onboardingQueue';
import { Briefcase, UsersRound, Settings, ArrowRight, ArrowLeft } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const DONE_KEY = 'ht_welcome_tour_done';

const STEPS = [
  {
    selector: '[data-testid="sidebar-jobs"]',
    icon: Briefcase,
    title: 'Jobs',
    body: 'Create, assign and track every job here — from scheduling right through to invoicing.',
  },
  {
    selector: '[data-testid="sidebar-team management"]',
    icon: UsersRound,
    title: 'Team Management',
    body: 'Build teams, assign supervisors, and set up on-site kiosks and QR clock-in.',
  },
  {
    selector: '[data-testid="sidebar-settings"]',
    icon: Settings,
    title: 'Settings',
    body: 'Company info, customers, projects, QuickBooks and everything else lives here.',
  },
];

export const WelcomeTour = () => {
  const { user, token } = useAuth();
  const [wants, setWants] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const show = usePromptSlot('tour', wants);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    if (localStorage.getItem(DONE_KEY) === '1') return;
    if (window.innerWidth < 768) return;
    axios.get(`${API}/onboarding/status`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((res) => {
        if (!res.data?.all_core_done) setWants(true);
      })
      .catch(() => {});
  }, [user?.role, token]);

  const measure = useCallback(() => {
    const el = document.querySelector(STEPS[step].selector);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: 'nearest' });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    if (!show) return;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [show, measure]);

  if (!show) return null;

  const finish = () => {
    localStorage.setItem(DONE_KEY, '1');
    setWants(false);
  };

  const s = STEPS[step];
  const Icon = s.icon;

  return (
    <div className="fixed inset-0 z-[90]" data-testid="welcome-tour">
      {/* Dimmed backdrop */}
      <div className="absolute inset-0" style={{ background: 'rgba(5,10,18,0.72)' }} onClick={finish} />

      {/* Spotlight ring over the sidebar item */}
      {rect && (
        <div
          className="absolute rounded-xl pointer-events-none transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            border: '2px solid #1E88E5',
            boxShadow: '0 0 0 6px rgba(30,136,229,0.25), 0 0 40px rgba(30,136,229,0.35)',
            background: 'rgba(30,136,229,0.08)',
          }}
          data-testid="welcome-tour-spotlight"
        />
      )}

      {/* Step card — sits to the right of the sidebar */}
      <div
        className="absolute w-80 rounded-2xl p-5 transition-all duration-300"
        style={{
          top: rect ? Math.min(Math.max(rect.top - 20, 16), window.innerHeight - 240) : '30%',
          left: rect ? rect.left + rect.width + 24 : '35%',
          background: '#0F1923',
          border: '1px solid #2D3748',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
        data-testid="welcome-tour-card"
      >
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,136,229,0.15)' }}>
            <Icon className="w-5 h-5" style={{ color: '#1E88E5' }} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748B' }}>
              Welcome tour · {step + 1} of {STEPS.length}
            </p>
            <h3 className="text-white font-semibold text-base leading-tight" data-testid="welcome-tour-title">{s.title}</h3>
          </div>
        </div>
        <p className="text-sm mb-4" style={{ color: '#A0AEC0' }}>{s.body}</p>
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={finish}
            className="text-xs hover:underline"
            style={{ color: '#64748B' }}
            data-testid="welcome-tour-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep((v) => v - 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-white/5"
                style={{ color: '#A0AEC0', border: '1px solid #2D3748' }}
                data-testid="welcome-tour-back"
              >
                <ArrowLeft className="w-3.5 h-3.5 inline mr-1" />Back
              </button>
            )}
            <button
              type="button"
              onClick={() => (step < STEPS.length - 1 ? setStep((v) => v + 1) : finish())}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white ht-btn-glow"
              style={{ background: '#1E88E5' }}
              data-testid="welcome-tour-next"
            >
              {step < STEPS.length - 1 ? <>Next<ArrowRight className="w-3.5 h-3.5 inline ml-1" /></> : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
